/**
 * REGISTA AI: Authentication Context and Provider
 * 
 * Simplified auth context that wraps the API client.
 * Provides authentication state and methods using D1 backend.
 */

import React, { createContext, useContext } from 'react';
import { useApi } from './api-client';

interface AuthState {
  user: any | null;
  loading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isLoading, isAuthenticated, error, login, logout } = useApi();

  const value: AuthState = {
    user,
    loading: isLoading,
    isAuthenticated,
    error,
    signIn: login,
    signOut: logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Auth guard component
interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!user) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-display font-semibold text-dark-900 mb-2">
              Sign in required
            </h2>
            <p className="text-dark-600 mb-4">
              Please sign in to access this page.
            </p>
            <a
              href="/auth/login"
              className="btn btn-primary btn-md"
            >
              Sign In
            </a>
          </div>
        </div>
      )
    );
  }

  return <>{children}</>;
}